import { chunk } from 'lodash-es';
import {
  Task,
  taskCountRespZod,
  tasksRespZod,
} from 'packages/lib/api/annotations/contract/task';
import { AnnotationState } from 'packages/lib/api/annotations/contract/enums';
import { apiGet } from 'packages/lib/api/annotations/rest';
import z from 'zod';
import {
  CameraChannelStrategyParams,
  PickupStrategy,
} from 'packages/lib/api/annotations-ops/contract';
import { getRandomElements } from 'packages/task-man/service/utilities';
import { taskManApi } from 'packages/lib/api/annotations-ops/clients';

// 2026-09-14 Used Cursor Auto for rough draft

export type ApplyPickupStrategyParams = {
  taskGroupToken: string;
  annotationState: AnnotationState;
  userEmail: string;
  n: number;
  strategy: PickupStrategy;
};

export async function applyPickupStrategy(
  params: ApplyPickupStrategyParams
): Promise<Task[]> {
  switch (params.strategy.strategy_type) {
    case 'FIFO':
      return executeFifoStrategy(params);
    case 'RANDOM':
      return executeRandomStrategy(params);
    case 'FRAME_COUNT':
      return executeFrameCountStrategy(params);
    case 'ANNOTATION_COUNT':
      return executeAnnotationCountStrategy(params);
    case 'CAMERA_CHANNEL':
      return executeCameraChannelStrategy(params);
    default: {
      return executeFifoStrategy(params);
    }
  }
}

export const lensAnnotationCountByTaskZod = z.record(z.string(), z.number());
const COUNT_BATCH_SIZE = 40; // Unbounded count queries can hit a 414 URI too long
const FETCH_CONCURRENCY = 5; // Limit Promise.all concurrency
const FULL_POOL_PAGE_SIZE = 2000; // The largest we can fetch at one time
const MAX_PAGES_SAFETY_CAP = 100; // For safety when accumulating pages

async function executeFifoStrategy({
  taskGroupToken,
  annotationState,
  userEmail,
  n,
}: ApplyPickupStrategyParams): Promise<Task[]> {
  const pageSize = Math.max(n, 50);
  const completedTokens = await fetchCompletedTaskTokens(userEmail);
  const available: Task[] = [];

  for (let pageNo = 1; pageNo <= MAX_PAGES_SAFETY_CAP; pageNo++) {
    const page = await fetchUnassignedTasksPage(
      taskGroupToken,
      annotationState,
      pageSize,
      pageNo
    );
    available.push(...filterCompletedTasks(page, completedTokens));

    if (available.length >= n || page.length < pageSize) {
      break;
    }
  }

  return available.slice(0, n);
}

async function executeRandomStrategy(
  params: ApplyPickupStrategyParams
): Promise<Task[]> {
  const availableTasks = await fetchAvailableUnassignedTasks(params);
  return getRandomElements(availableTasks, params.n);
}

async function executeFrameCountStrategy(
  params: ApplyPickupStrategyParams
): Promise<Task[]> {
  const availableTasks = await fetchAvailableUnassignedTasks(params);

  return availableTasks
    .map((task) => ({
      task,
      frameCount: Math.max(0, task.endFrame - task.startFrame),
    }))
    .sort((a, b) => b.frameCount - a.frameCount)
    .slice(0, params.n)
    .map((item) => item.task);
}

async function executeAnnotationCountStrategy(
  params: ApplyPickupStrategyParams
): Promise<Task[]> {
  const availableTasks = await fetchAvailableUnassignedTasks(params);
  const countsByTaskToken = await fetchLensAnnotationCountsByTaskToken(
    availableTasks.map((task) => task.token)
  );

  return availableTasks
    .map((task) => ({
      task,
      count: countsByTaskToken[task.token] ?? 0,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, params.n)
    .map((item) => item.task);
}

async function executeCameraChannelStrategy(
  params: ApplyPickupStrategyParams
): Promise<Task[]> {
  const { channels } = params.strategy
    .strategy_params as CameraChannelStrategyParams;

  const availableTasks = await fetchAvailableUnassignedTasks(params, {
    withData: true,
  });

  const channelMap = new Map<string, number>(
    channels.map((channel, index) => [channel, index])
  );

  const getPriorityRank = (task: Task): number => {
    const channel = task.videoExtraction?.channel;
    if (!channel) return channels.length + 1;
    return channelMap.get(channel) ?? channels.length;
  };

  // Pre-allocate buckets for ranked channels + unknown channel + missing channel
  const buckets: Task[][] = Array.from(
    { length: channels.length + 2 },
    () => []
  );

  for (const task of availableTasks) {
    const rank = getPriorityRank(task);
    buckets[rank].push(task);
  }

  const selected: Task[] = [];
  let needed = params.n;

  for (const bucket of buckets) {
    if (needed <= 0) break;
    if (bucket.length === 0) continue;

    const picked = getRandomElements(bucket, needed);
    selected.push(...picked);
    needed -= picked.length;
  }

  return selected;
}

// ----------------- Utility methods --------------------
type WithData = { withData?: boolean };

async function fetchAvailableUnassignedTasks(
  params: ApplyPickupStrategyParams,
  { withData }: WithData = {}
) {
  const [allTasks, completedTokens] = await Promise.all([
    fetchAllUnassignedTasks(params.taskGroupToken, params.annotationState, {
      withData,
    }),
    fetchCompletedTaskTokens(params.userEmail),
  ]);

  const availableTasks = filterCompletedTasks(allTasks, completedTokens);

  return availableTasks;
}

function unassignedTasksQuery(
  taskGroupToken: string,
  annotationState: AnnotationState
): string {
  return `taskGroupToken=${taskGroupToken}&assignee=%null&annotationState=${annotationState}&orderBy=createdAt`;
}

function fetchUnassignedTasksPage(
  taskGroupToken: string,
  annotationState: AnnotationState,
  pageSize: number,
  pageNo: number,
  { withData }: WithData = {}
): Promise<Task[]> {
  const baseUrl = withData ? '/tasks/data' : '/tasks';
  const baseSearch = unassignedTasksQuery(taskGroupToken, annotationState);
  return apiGet(
    `${baseUrl}?${baseSearch}&pageSize=${pageSize}&pageNo=${pageNo}`,
    tasksRespZod.parse
  );
}

async function fetchCompletedTaskTokens(
  userEmail: string
): Promise<Set<string>> {
  const history = await taskManApi.history.get(userEmail);
  return new Set((history ?? []).map((event) => event.task_token));
}

function filterCompletedTasks(
  tasks: Task[],
  completedTokens: Set<string>
): Task[] {
  return tasks.filter((task) => !completedTokens.has(task.token));
}

async function fetchAllUnassignedTasks(
  taskGroupToken: string,
  annotationState: AnnotationState,
  { withData }: WithData = {}
): Promise<Task[]> {
  const query = unassignedTasksQuery(taskGroupToken, annotationState);
  const countResp = await apiGet(
    `/tasks/count?${query}`,
    taskCountRespZod.parse
  );
  const total = countResp['*'];

  if (typeof total !== 'number' || total === 0) {
    return [];
  }

  const numPages = Math.min(
    Math.ceil(total / FULL_POOL_PAGE_SIZE),
    MAX_PAGES_SAFETY_CAP
  );
  const pageNos = Array.from({ length: numPages }, (_, i) => i + 1);
  const pages: Task[][] = [];

  for (const pageChunk of chunk(pageNos, FETCH_CONCURRENCY)) {
    const fetched = await Promise.all(
      pageChunk.map((pageNo) =>
        fetchUnassignedTasksPage(
          taskGroupToken,
          annotationState,
          FULL_POOL_PAGE_SIZE,
          pageNo,
          { withData }
        )
      )
    );
    pages.push(...fetched);
  }

  return pages.flat();
}

async function fetchLensAnnotationCountsByTaskToken(
  taskTokens: string[]
): Promise<Record<string, number>> {
  if (taskTokens.length === 0) {
    return {};
  }

  const batches = chunk(taskTokens, COUNT_BATCH_SIZE);
  const results: Record<string, number>[] = [];

  for (const batchChunk of chunk(batches, FETCH_CONCURRENCY)) {
    const chunkResults = await Promise.all(
      batchChunk.map((batch) =>
        apiGet(
          `/lens-annotations/count?taskToken=${batch.join(
            ','
          )}&groupBy=taskToken`,
          lensAnnotationCountByTaskZod.parse
        )
      )
    );
    results.push(...chunkResults);
  }

  return Object.assign({}, ...results);
}
